import { useState, useEffect } from "react";
import { FinanceService } from "../services/financeService";
import { Sheet, Category, CategoryType, FinancialEntry } from "@/types/finance";
import { authApiV2 } from "@/services/apiAuth";
import { categoriesApiV2, financialEntriesApiV2 } from "@/services/apiV2";
import { MonthYear } from "@/types/monthYear";

export const useFinanceData = () => {
  const [categories, setCategories] = useState<Category[]>(() => {
    const saved = FinanceService.getCategories();
    return saved.length > 0 ? saved : [];
  });

  const [sheets, setSheets] = useState<Sheet[]>([]);

  const [monthlyValues, setMonthlyValues] = useState<FinancialEntry[]>([]);

  const getMonthlyValues = async (sheetId: string) => {
    const values = await FinanceService.getMonthlyValues(sheetId);
    setMonthlyValues(values);
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const next = await FinanceService.getSheets();
        if (isMounted) setSheets(next);
      } catch (e) {
        // Em caso de token ausente/expirado, não quebra o app.
        if (isMounted) setSheets([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    FinanceService.saveCategories(categories);
  }, [categories]);

  useEffect(() => {
    FinanceService.saveSheets(sheets);
  }, [sheets]);

  useEffect(() => {
    FinanceService.saveMonthlyValues(monthlyValues);
  }, [monthlyValues]);

  const addCategory = (
    name: string,
    type: CategoryType,
    parentId: string | null,
    sheet_id: string
  ) => {
    const newCat: Category = {
      id: crypto.randomUUID(),
      name: name,
      type: type,
      parentId: parentId,
      sheet_id: sheet_id,
      order: 0,
      isExpanded: true,
    };

    categoriesApiV2.create(newCat).then((res) => {
      categoriesApiV2.getAll(sheet_id).then((res) => {
        setCategories(res);
      });
    });
  };

  const updateCategory = (
    id: string,
    name: string,
    type: CategoryType,
    parentId: string | null
  ) => {
    categoriesApiV2.update(id, { name, type, parentId }).then((res) => {
      setCategories((prev) =>
        prev.map((cat) =>
          cat.id === id ? { ...cat, name, type, parentId } : cat
        )
      );
    });
    // setCategories((prev) =>
    //   prev.map((cat) =>
    //     cat.id === id ? { ...cat, name, type, parentId } : cat
    //   )
    // );
  };

  const deleteCategory = (id: string) => {
    const idsToDelete = FinanceService.getIdsToDelete(id, categories);
    setCategories((prev) => prev.filter((c) => !idsToDelete.includes(c.id)));
    setMonthlyValues((prev) =>
      prev.filter((v) => !idsToDelete.includes(v.category_id))
    );
  };

  const addSheet = (name: string) => {
    let userId = "";
    try {
      userId = authApiV2.getUser()?.id ?? "";
    } catch {
      userId = "";
    }
    const newSheet: Sheet = {
      id: crypto.randomUUID(),
      title: name,
      user_id: userId,
      principal: false,
      created_at: new Date(),
    };
    setSheets((prev) => [...prev, newSheet]);
    return newSheet.id;
  };

  const updateValue = (
    sheetId: string,
    category: Category,
    monthYear: MonthYear,
    amount: number
  ) => {
    financialEntriesApiV2
      .atualizarInserir({
        category: category,
        amount: amount,
        year: monthYear.year,
        month: monthYear.month - 1,
        sheet_id: sheetId,
        status: "paid",
      })
      .then((entry: FinancialEntry) => {
        setMonthlyValues((prev) => {
          const existingIdx = prev.findIndex(
            (v) =>
              v.sheet_id === sheetId &&
              v.category_id === category.id &&
              v.year === monthYear.year &&
              v.month + 1 === monthYear.month
          );

          if (existingIdx >= 0) {
            const next = [...prev];
            next[existingIdx] = { ...next[existingIdx], amount };
            return next;
          }
          return [...prev, entry];
        });
      });
  };

  return {
    categories,
    setCategories,
    sheets,
    setSheets,
    getMonthlyValues,
    monthlyValues,
    setMonthlyValues,
    addCategory,
    updateCategory,
    deleteCategory,
    addSheet,
    updateValue,
  };
};
